import { WidgetType } from '../../enums/widget-type.enum';
import { ConfigStore } from '../../store/config';
import { defaultLogger as logger } from '../../telemetry/logger';
import { get } from 'svelte/store';
import { GetThread } from '../api/get-thread';
import { ConversationHistoryHelper } from '../../helpers/conversation-history-helper';
import type { IAnswer } from '../../interfaces/answer.interface';
import {
  state_conversationResults,
  state_updatedAttributes,
} from '../../store/state';
import { CitationHelper } from '../../helpers/citation-helper';
import { MarkupHelper } from '../../helpers/markup-helper';
import { WindowsCustomEventTypes } from '../../enums/windows-custom-event.enum';
import { QuestionRelatedSuggestionsHandler } from './question-related-suggestions.handler';
import { conversationConnection } from '../api/conversation-connection';
import { ChatKitWidgetHelper } from '../../helpers/chatkit-widget-helper';
import { ChatKitWidgetCache } from '../../helpers/chatkit-widget-cache';
import type { IToolCall } from '../../interfaces/tool-call.interface';
import type { IChatKitWidgetInstance } from '../../interfaces/chatkit-widget.interface';

export class ThreadHandler {
  private questionRelatedSuggestionsHandler: QuestionRelatedSuggestionsHandler;
  private md;

  constructor() {
    this.questionRelatedSuggestionsHandler =
      new QuestionRelatedSuggestionsHandler();
    this.md = new MarkupHelper();
  }

  public async get(preservePending: boolean = false) {
    if (
      [WidgetType.fb].includes(get(ConfigStore).widgetType) ||
      !ConversationHistoryHelper.getThreadId()
    )
      return;

    try {
      const res = await GetThread(ConversationHistoryHelper.getThreadId() ?? '');

      if (res?.ok) {
        const thread = await res.json();
        //console.dir(thread);
        const answers: IAnswer[] = [];
        const widgetOwners = this.resolveWidgetOwners(thread.hits);
        for (let i = 0; i < thread.hits.length; i++) {
          // console.log("ThreadHandler/get", thread.hits[i].data.answer);
          const questionId = thread.hits[i].id;
          const threadId =
            thread.hits[i].data.conversation?.thread_id ??
            ConversationHistoryHelper.getThreadId() ??
            questionId;
          const toolCalls = this.extractToolCalls(thread.hits[i]);
          const widgets = this.resolveWidgets(
            toolCalls,
            threadId,
            questionId,
            widgetOwners
          );
          if (
            widgets.length ||
            thread.hits[i].data.answer?.content ||
            (i > 0 &&
              !thread.hits[i - 1].data.question &&
              thread.hits[i - 1].data.answer?.content)
          ) {
            const answer: IAnswer = {
              question: thread.hits[i].data.question?.trim()
                ? thread.hits[i].data.question
                : '',
              thread_id: threadId,
              answer: this.md.render(
                thread.hits[i].data.answer?.content ?? '',
                get(ConfigStore).whitelistedDomains
              ),
              reasoning: thread.hits[i].data.answer?.reasoning,
              notes: thread.hits[i].data.answer?.notes,
              question_files: thread.hits[i].data.answer?.question_files,
              answer_chunks: [],
              tool_calls: toolCalls.length ? toolCalls : undefined,
              widgets: widgets.length ? widgets : undefined,
              question_id: questionId,
              document_data: thread.hits[i].data.answer?.document_data,
              document_id: thread.hits[i].data.answer?.document_id,
              is_greeting:
                (!thread.hits[i].data.conversation?.thread_id &&
                  !thread.hits[i].data.conversation?.source) ||
                thread.hits[i].data.conversation?.thread_id ==
                thread.hits[i].id,
              has_unfinished_citation: false,
              position_in_thread: 0, // not required
              client_message_id:
                thread.hits[i].data.conversation?.client_message_id,
              request_id: thread.hits[i].data.conversation?.request_id,
            };
            CitationHelper.addReferences(answer);
            answers.push(answer);
          }
        }
        /*const answers: IAnswer[] = thread.hits.map(h => {
                    const answer: IAnswer = {
                        question: h.data.question,
                        thread_id: h.data.conversation?.thread_id,
                        answer: h.data.answer.content ? this.md.render(h.data.answer.content) : h.data.answer.content,
                        answer_chunks: [],
                        tool_calls: null,
                        question_id: h.id,
                        document_data: h.data.answer.document_data,
                        document_id: h.data.answer.document_id,
                        is_greeting: !h.data.conversation?.thread_id || (h.data.conversation?.thread_id == h.id),
                        has_unfinished_citation: false,
                        position_in_thread: null // not required
                    };
                    CitationHelper.addReferences(answer);
                    return answer;
                });*/
        answers.reverse();

        if (preservePending) {
          const pending = get(state_conversationResults).filter(
            (item) =>
              item.is_streaming &&
              !answers.some(
                (answer) =>
                  !!answer.client_message_id &&
                  answer.client_message_id === item.client_message_id
              )
          );
          ConversationHistoryHelper.populateThread([...answers, ...pending]);
        } else {
          ConversationHistoryHelper.populateThread(answers);
        }

        if (answers.length > 0) {
          if (
            get(ConfigStore).suggestQuestions === 'always' ||
            (get(ConfigStore).suggestQuestions === 'greeting' &&
              answers.length == 1) ||
            (get(ConfigStore).suggestQuestions === 'interaction' &&
              answers.length > 1)
          ) {
            this.questionRelatedSuggestionsHandler.get(
              answers[answers.length - 1], answers.length == 1
            );
          }
        }

        window.dispatchEvent(
          new CustomEvent(WindowsCustomEventTypes.greetingInitCompleted)
        );
        //console.dir(answers);
      }
    } catch (e) {
      logger.error('ThreadHandler/get', e);
    }
  }

  private extractToolCalls(hit: any): IToolCall[] {
    const answer = hit?.data?.answer;
    const toolCalls =
      answer?.tool_calls ??
      answer?.function_call ??
      answer?.toolCalls ??
      [];
    return Array.isArray(toolCalls) ? toolCalls : [];
  }

  private renderWidgets(
    toolCalls: IToolCall[],
    questionId: string
  ): IChatKitWidgetInstance[] {
    return toolCalls
      .map((toolCall) => {
        const rendered = ChatKitWidgetHelper.renderToolCall(toolCall);
        if (!rendered) return null;
        rendered.instance.questionId = questionId;
        return rendered.instance;
      })
      .filter((widget): widget is IChatKitWidgetInstance => !!widget);
  }

  private resolveWidgets(
    toolCalls: IToolCall[],
    threadId: string,
    questionId: string,
    widgetOwners: ReadonlyMap<string, string>
  ): IChatKitWidgetInstance[] {
    const ownedToolCalls = toolCalls.filter((toolCall) => {
      if (!toolCall.id) return true;
      const owner = widgetOwners.get(toolCall.id);
      return !owner || owner === questionId;
    });
    const rendered = this.renderWidgets(ownedToolCalls, questionId);
    if (rendered.length) {
      ChatKitWidgetCache.saveWidgets(threadId, questionId, rendered);
      return rendered;
    }

    return ChatKitWidgetCache.getWidgets(threadId, questionId)
      .filter((widget) => {
        if (!widget.toolCallId) return true;
        const owner = widgetOwners.get(widget.toolCallId);
        return !owner || owner === questionId;
      })
      .map((widget) => ({
        ...widget,
        questionId: widget.questionId ?? questionId,
      }));
  }

  private resolveWidgetOwners(hits: any[]): Map<string, string> {
    const owners = new Map<string, string>();
    const chronologicalHits = [...hits].reverse().sort((left, right) => {
      const leftDate = Date.parse(
        left?.data?.created_date ?? left?.data?.['@timestamp'] ?? ''
      );
      const rightDate = Date.parse(
        right?.data?.created_date ?? right?.data?.['@timestamp'] ?? ''
      );
      return Number.isNaN(leftDate) || Number.isNaN(rightDate)
        ? 0
        : leftDate - rightDate;
    });

    chronologicalHits.forEach((hit) => {
      const questionId = hit?.id;
      if (!questionId) return;
      this.extractToolCalls(hit)
        .filter((toolCall) => ChatKitWidgetHelper.isWidgetToolCall(toolCall))
        .forEach((toolCall) => {
          if (toolCall.id && !owners.has(toolCall.id)) {
            owners.set(toolCall.id, questionId);
          }
        });
    });

    chronologicalHits.forEach((hit) => {
      const questionId = hit?.id;
      if (!questionId) return;
      const threadId =
        hit?.data?.conversation?.thread_id ??
        ConversationHistoryHelper.getThreadId() ??
        questionId;
      ChatKitWidgetCache.getWidgets(threadId, questionId).forEach((widget) => {
        if (widget.toolCallId && !owners.has(widget.toolCallId)) {
          owners.set(widget.toolCallId, questionId);
        }
      });
    });

    let renderOwners = new Map<string, string>();
    chronologicalHits.forEach((hit) => {
      const questionId = hit?.id;
      if (!questionId) return;
      if (hit?.data?.question?.trim()) {
        renderOwners = new Map<string, string>();
      }
      const threadId =
        hit?.data?.conversation?.thread_id ??
        ConversationHistoryHelper.getThreadId() ??
        questionId;
      const ownedToolCalls = this.extractToolCalls(hit).filter(
        (toolCall) => !toolCall.id || owners.get(toolCall.id) === questionId
      );
      const rendered = this.renderWidgets(ownedToolCalls, questionId);
      const candidates = rendered.length
        ? rendered
        : ChatKitWidgetCache.getWidgets(threadId, questionId).filter(
            (widget) =>
              !widget.toolCallId ||
              owners.get(widget.toolCallId) === questionId
          );

      candidates.forEach((widget) => {
        const renderKey = ChatKitWidgetHelper.getRenderKey(widget);
        const existingOwner = renderOwners.get(renderKey);
        if (existingOwner && widget.toolCallId) {
          owners.set(widget.toolCallId, existingOwner);
          return;
        }
        renderOwners.set(renderKey, questionId);
      });
    });

    return owners;
  }

  public clear() {
    ChatKitWidgetCache.clearThread(ConversationHistoryHelper.getThreadId());
    conversationConnection.close();
    state_updatedAttributes.set(null);
    ConversationHistoryHelper.clear();
  }
}
